/**
 * App Version Check
 *
 * Periodically polls build-meta.json to detect new deployments.
 * When a new build is detected, the page is automatically reloaded
 * so the user always has the latest version.
 */

const VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // Poll every 5 minutes
const BUILD_META_URL = "/build-meta.json";

let currentBuildId: string | null = null;
let versionCheckTimer: ReturnType<typeof setInterval> | null = null;
let isCheckingVersion = false;

async function fetchBuildMeta(): Promise<string | null> {
  try {
    // Bypass all caches with timestamp query param and cache-control headers
    const response = await fetch(`${BUILD_META_URL}?_t=${Date.now()}`, {
      cache: "no-store",
      headers: {
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
      },
    });
    if (!response.ok) return null;
    const data = await response.json();
    return data.buildId || null;
  } catch {
    // build-meta.json may not exist in local dev — silently ignore
    return null;
  }
}

async function checkForUpdate(): Promise<void> {
  if (isCheckingVersion) return;
  isCheckingVersion = true;

  try {
    const latestBuildId = await fetchBuildMeta();
    if (!latestBuildId) return;

    if (currentBuildId && latestBuildId !== currentBuildId) {
      console.log(
        `New build detected: ${currentBuildId} → ${latestBuildId}. Reloading...`
      );
      stopVersionCheck();
      window.location.reload();
    } else if (!currentBuildId) {
      currentBuildId = latestBuildId;
    }
  } finally {
    isCheckingVersion = false;
  }
}

/**
 * Start polling for new build versions.
 * Call this once when the application initializes.
 */
export function startVersionCheck(): void {
  // Fetch the initial build ID on startup
  fetchBuildMeta().then((buildId) => {
    if (buildId) {
      currentBuildId = buildId;
      console.log(`Current build ID: ${currentBuildId}`);
    }
  });

  // Start periodic polling
  if (!versionCheckTimer) {
    versionCheckTimer = setInterval(checkForUpdate, VERSION_CHECK_INTERVAL);
  }

  // Also check when the user returns to the tab (visibility change)
  document.addEventListener("visibilitychange", onVisibilityChange);
}

function onVisibilityChange(): void {
  if (document.visibilityState === "visible") {
    checkForUpdate();
  }
}

/**
 * Stop polling for version changes.
 */
export function stopVersionCheck(): void {
  if (versionCheckTimer) {
    clearInterval(versionCheckTimer);
    versionCheckTimer = null;
  }
  document.removeEventListener("visibilitychange", onVisibilityChange);
}
